import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Comercio } from 'src/app/models/Comercio';
import { Horario } from 'src/app/models/Horario';
import { Ubicacion } from 'src/app/models/Ubicacion';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Location } from '@angular/common';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MAP_ID } from 'src/environments/environment';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoadingService } from 'src/app/services/loading.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
    selector: 'app-comercio',
    templateUrl: './comercio.component.html',
    styleUrls: ['./comercio.component.css'],
    standalone: false
})
export class ComercioComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('divMap') divMap!: ElementRef;
  private _router = inject(Router);
  private _location = inject(Location);
  private _usuarioService = inject(UsuarioService);
  private _orderComercioService = inject(OrderComercioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  private destroy$ = new Subject<void>();

  // ---- estado UI ----
  error = signal<string>('');

  // loading "local" (mapa / búsqueda), distinto del spinner global del AppComponent
  loading = signal<boolean>(true);

  // tooltip
  showHelp = signal<boolean>(false);
  helpText = computed(() => 'Seleccioná un comercio cercano de la lista para avanzar.');

  // usuario/perfil
  id_perfil = signal<number>(2);
  id_cliente = signal<string>('');

  // mapa/geo
  n_latitud = signal<number>(-34.75989677508757);
  n_longitud = signal<number>(-58.406636762250805);
  x_zona = signal<string>('');

  map: any;
  marker: any;
  distanciaKM = 40;

  // listado
  comercioUbicacionHorarios = signal<comercioUbicacionHorarios[]>([]);

  // ubicacion default (BehaviorSubject -> signal)
  ubicacionDefaultSig = toSignal(this._usuarioService.ubicacion_default, {
    initialValue: this._usuarioService.ubicacion_default.getValue(),
  });

  private mySubscriptionUbicacionDefault?: Subscription;
  private mapIniciado = false;

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;
    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
        const user = this._usuarioService.usuario();
        this.id_perfil.set(user?.id_perfil ?? 2);
        if (user?.id) {
          this._orderComercioService.ordenComercio.id_cliente = user.id;
        }
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
        return;
      }

      // Inicializo valores de ubicación desde orden o desde ubicación default
      const ord = this._orderComercioService.ordenComercio;
      const ubiDef = this.ubicacionDefaultSig();
      const lat =
        ord.n_latitud ??
        ubiDef?.n_latitud ??
        -34.75989677508757;
      const lng =
        ord.n_longitud ??
        ubiDef?.n_longitud ??
        -58.406636762250805;
      this.n_latitud.set(lat);
      this.n_longitud.set(lng);
      this.x_zona.set(ord.x_zona ?? '');
    });
  }

  async ngAfterViewInit(): Promise<void> {
    // Si NO tengo coordenadas en la orden, me engancho a cambios de ubicacion_default
    const ord = this._orderComercioService.ordenComercio;
    if (!ord.n_latitud && !ord.n_longitud) {
      this.mySubscriptionUbicacionDefault =
        this._usuarioService.ubicacion_default
          .pipe(takeUntil(this.destroy$))
          .subscribe(async (ubicacionDefault) => {
            const lat = ubicacionDefault?.n_latitud;
            const lng = ubicacionDefault?.n_longitud;
            if (lat && lng) {
              this.n_latitud.set(lat);
              this.n_longitud.set(lng);
              await this.iniciarMapaUnaVez();
            } else {
              this.loading.set(false);
            }
          });
    } else {
      await this.iniciarMapaUnaVez();
    }
  }

  ngOnDestroy(): void {
    if (this.mySubscriptionUbicacionDefault) {
      this.mySubscriptionUbicacionDefault.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- HELP ----------------
  toggleHelp(): void {
    this.showHelp.update(v => !v);
  }

  // ---------------- NAV ----------------
  back(): void {
    this._location.back();
  }

  cambiarUbicacion(): void {
    if (this.mySubscriptionUbicacionDefault) {
      this.mySubscriptionUbicacionDefault.unsubscribe();
    }
    this._router.navigate(['/seleccion-ubicacion-default']);
  }

  isActiveUbicacion(id_ubicacion: number): boolean {
    return this._orderComercioService.ordenComercio.id_ubicacion === id_ubicacion;
  }

  async saveComercio(id_ubicacion: number): Promise<void> {
    this.loading.set(true);
    const lista = this.comercioUbicacionHorarios();
    const seleccionado = lista.find(x => x.id_ubicacion === id_ubicacion);
    if (!seleccionado?.comercio) {
      this.error.set('No se pudo seleccionar el comercio.');
      this.loading.set(false);
      return;
    }

    const ord = this._orderComercioService.ordenComercio;
    ord.id_cliente = this.id_cliente() || ord.id_cliente;
    ord.id_comercio = seleccionado.comercio.id;
    this._orderComercioService.comercio = seleccionado.comercio;
    ord.id_ubicacion = id_ubicacion;
    this._orderComercioService.ubicacion = seleccionado.ubicacion;
    const ubiDef = this.ubicacionDefaultSig();

    // zona
    const zona = this.x_zona()?.trim();
    if (!zona) {
      ord.x_zona =
        (ubiDef?.x_domicilio ?? '') +
        '. Piso: ' + (ubiDef?.x_piso_dpto ?? '') +
        '. CP: ' + (ubiDef?.x_codigo_postal ?? '');
    } else {
      ord.x_zona = zona;
    }
    ord.n_latitud = this.n_latitud();
    ord.n_longitud = this.n_longitud();
    this.loading.set(false);
    this._router.navigate(['/pedido']);
  }

  // ---------------- MAPA ----------------
  private async waitForGoogleMaps(timeoutMs = 12000): Promise<void> {
    const start = Date.now();
    while (!(window as any).google?.maps?.importLibrary) {
      if (Date.now() - start > timeoutMs) {
        throw new Error('Google Maps no cargó a tiempo.');
      }
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  private async iniciarMapaUnaVez(): Promise<void> {
    if (this.mapIniciado) return;
    this.mapIniciado = true;
    await this.cargarMapa(this.n_latitud(), this.n_longitud());
  }

  async cargarMapa(latitude: number, longitude: number): Promise<void> {
    try {
      this.loading.set(true);
      await this.waitForGoogleMaps();
      const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      const { InfoWindow } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;

      // crear mapa solo una vez
      if (!this.map) {
        this.map = new Map(this.divMap.nativeElement, {
          center: { lat: latitude, lng: longitude },
          zoom: 13,
          mapId: MAP_ID,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
        });

        this.marker = new AdvancedMarkerElement({
          map: this.map,
          position: { lat: latitude, lng: longitude },
          title: 'Ubicación',
        });
      } else {
        this.map.setCenter({ lat: latitude, lng: longitude });

        if (this.marker) {
          this.marker.position = { lat: latitude, lng: longitude };
        }
      }

      // BUSCO TODAS LAS UBICACIONES CERCANAS
      this._orderComercioService
        .getUbicacionesComercioCercanas(latitude, longitude, this.distanciaKM)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (ubi) => {
            const nuevos: comercioUbicacionHorarios[] = [];
            if (ubi?.length > 0) {
              ubi.forEach((ubic) => {
                const temp: comercioUbicacionHorarios = {
                  id_ubicacion: ubic.id!,
                  ubicacion: ubic,
                };

                // COMERCIO
                this._usuarioService
                  .getComercioXId(ubic.id_usuario!)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe({
                    next: (comer) => {
                      if (!comer) return;
                      temp.comercio = comer;

                      // HORARIOS
                      this._usuarioService
                        .getHorario(ubic.id_usuario!)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                          next: (hor) => {
                            temp.horarios = hor ?? undefined;
                            // abierto/cerrado
                            temp.b_abierto = false;

                            if (hor) {
                              const diaActual = new Date().getDay();
                              const horaActual = new Date().getHours();
                              const minutoActual = new Date().getMinutes();
                              const actual = new Date().setHours(horaActual, minutoActual);

                              const desdeStr = (hor as any)[`x_${this.diaNombre(diaActual)}_desde`];
                              const hastaStr = (hor as any)[`x_${this.diaNombre(diaActual)}_hasta`];

                              const dia_desde = desdeStr
                                ? new Date().setHours(+desdeStr.slice(0, 2), +desdeStr.slice(3, 5))
                                : null;

                              const dia_hasta = hastaStr
                                ? new Date().setHours(+hastaStr.slice(0, 2), +hastaStr.slice(3, 5))
                                : null;

                              temp.b_abierto =
                                !!(dia_desde && dia_hasta && actual >= dia_desde && actual <= dia_hasta);
                            }

                            // descuento máximo
                            this._orderComercioService
                              .getMaxProcentajeDescuentoComercio(comer.id!)
                              .pipe(takeUntil(this.destroy$))
                              .subscribe({
                                next: (maxProcentajeDescuento) => {
                                  temp.n_max_porcentaje_descuento_comercio =
                                    maxProcentajeDescuento >
                                    (comer.n_porcentaje_descuento_medicamentos ?? 0)
                                      ? maxProcentajeDescuento
                                      : comer.n_porcentaje_descuento_medicamentos;
                                },
                                error: () =>
                                  console.log('Error al obtener el Máximo % de descuento'),
                              });

                            // marker comercio
                            const infoWindow = new InfoWindow();
                            const marker = new AdvancedMarkerElement({
                              map: this.map,
                              position: { lat: ubic.n_latitud!, lng: ubic.n_longitud! },
                              title: temp.comercio!.x_nombre_fantasia,
                              content: (() => {
                                const img = document.createElement('img');
                                img.src = './assets/img/location_blue_x32.png';
                                img.alt = temp.comercio!.x_nombre_fantasia ?? '';
                                img.style.width = '32px';
                                img.style.height = '32px';
                                return img;
                              })(),
                            });
                            marker.addListener('click', () => {
                              infoWindow.close();
                              infoWindow.setContent(
                                `<div class='fw-bold'>${marker.title}</div>${ubic.x_domicilio}`
                              );
                              infoWindow.setPosition(marker.position);
                              infoWindow.open({ map: this.map });
                            });
                            // agrego al array
                            if (
                              temp.comercio?.x_razon_social &&
                              temp.comercio?.x_nombre_fantasia
                            ) {
                              nuevos.push(temp);
                              this.comercioUbicacionHorarios.set([...nuevos]);
                            }
                            this.loading.set(false);
                          },
                          error: (e) => {
                            console.error('Error al obtener horarios:', e);
                            this.loading.set(false);
                          },
                        });
                    },
                    error: () => {
                      console.log('Error al obtener el Comercio por id_usuario');
                      this.loading.set(false);
                    },
                  });
              });
            } else {
              this.comercioUbicacionHorarios.set([]);
              this.loading.set(false);
            }
          },
          error: (e) => {
            if (e?.status === 404) {
              console.warn('No existen Ubicaciones cercanas');
            } else {
              console.error('Error ubicaciones cercanas:', e);
            }
            this.comercioUbicacionHorarios.set([]);
            this.loading.set(false);
          },
          complete: () => console.log('Ubicaciones de comercio cercanas obtenidas'),
        });

    } catch (e) {
      console.error(e);
      this.error.set('No se pudo cargar el mapa.');
      this.loading.set(false);
    }
  }

  diaNombre(dia: number): string {
    return ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'][dia];
  }

  // helpers UI
  hasUbicacionDefault(): boolean {
    return !!this.ubicacionDefaultSig()?.x_domicilio;
  }

  ubicacionDefaultTexto(): string {
    return this.ubicacionDefaultSig()?.x_domicilio ?? '';
  }

}

interface comercioUbicacionHorarios {
  id_ubicacion: number;
  ubicacion: Ubicacion;
  comercio?: Comercio;
  horarios?: Horario;
  b_abierto?: boolean;
  n_max_porcentaje_descuento_comercio?: number;
}